/**
 * useCanvasCollab — keeps a canvas's state in sync across everyone in a
 * collab room (DeskeyPaint, DeskeyChart).
 *
 *   const { peerCount, status } = useCanvasCollab(roomId, {
 *     state: { objects, raster },          // serialisable fields to share
 *     apply: (patch) => { ... },           // replace local fields with a peer's
 *   });
 *
 * Wire format: `{ kind: 'sync', state: { <changed fields> } }`.
 *
 * - Local edits go out on a trailing-edge throttle, so the last change in a
 *   burst is always delivered.
 * - Only fields whose value changed are sent (a big raster isn't resent on
 *   every vector stroke).
 * - State applied from a peer becomes the baseline, so it is never echoed back.
 * - Joining never pushes the joiner's own (usually empty) canvas over the
 *   room; instead every existing peer answers a join with a full snapshot.
 */
import { useCallback, useEffect, useRef } from 'react';
import useCollabRoom from './useCollabRoom';

const SYNC_KIND = 'sync';
const DEFAULT_THROTTLE_MS = 150;

function changedFields(state, baseline) {
  return Object.keys(state).filter(k => state[k] !== baseline[k]);
}

function pick(state, keys) {
  return Object.fromEntries(keys.map(k => [k, state[k]]));
}

export default function useCanvasCollab(roomId, { state, apply, throttleMs = DEFAULT_THROTTLE_MS }) {
  const latestRef = useRef(state);
  latestRef.current = state;
  const applyRef = useRef(apply);
  applyRef.current = apply;
  // What every peer is known to have, per field: last sent or last applied.
  const baselineRef = useRef({});
  const sendRef = useRef(() => false);
  const timerRef = useRef(null);
  const lastSentAtRef = useRef(0);

  const flush = useCallback(() => {
    const current = latestRef.current;
    const keys = changedFields(current, baselineRef.current);
    if (!keys.length) return;
    const patch = pick(current, keys);
    if (sendRef.current({ kind: SYNC_KIND, state: patch })) {
      baselineRef.current = { ...baselineRef.current, ...patch };
      lastSentAtRef.current = Date.now();
    }
  }, []);

  const handleMessage = useCallback((msg) => {
    if (msg.kind === 'presence' && msg.event === 'join') {
      const snapshot = latestRef.current;
      if (sendRef.current({ kind: SYNC_KIND, state: snapshot })) {
        baselineRef.current = { ...snapshot };
      }
      return;
    }
    if (msg.kind !== SYNC_KIND || !msg.state || typeof msg.state !== 'object') return;
    baselineRef.current = { ...baselineRef.current, ...msg.state };
    applyRef.current(msg.state);
  }, []);

  const collab = useCollabRoom(roomId, { onMessage: handleMessage });
  sendRef.current = collab.send;

  // Whatever is on screen when a room starts is the baseline — never broadcast it unprompted.
  useEffect(() => {
    baselineRef.current = { ...latestRef.current };
  }, [roomId]);

  // Runs every render (state is a fresh object each time); the diff check keeps it cheap.
  // `collab.status` re-renders on open, which retries edits made while connecting.
  useEffect(() => {
    if (!roomId || timerRef.current) return;
    if (!changedFields(state, baselineRef.current).length) return;
    const wait = Math.max(0, lastSentAtRef.current + throttleMs - Date.now());
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      flush();
    }, wait);
  });

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, [roomId]);

  return collab;
}
